import React, { useEffect, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Grid, TextField, Divider, Button, Snackbar, Alert, Tabs, Tab, FormControlLabel, Switch, Autocomplete } from '@mui/material';
import { getDocument, findDocumentsByField, addDocument, updateDocument, getCollection } from '../services/firestoreService';
import { useAuth } from '../context/AuthContext';

const SurgicalProtocol = () => {
  const { surgeryId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { userData } = useAuth();

  const [surgery, setSurgery] = useState(null);
  const [protocolDoc, setProtocolDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const savingRef = useRef(false);

  const [surgeonProtocol, setSurgeonProtocol] = useState({
    preOpDiagnosis: '',
    postOpDiagnosis: '',
    description: '',
    findings: '',
    hasComplications: false,
    complications: '',
    postOpPlan: ''
  });

  const [anesthesiaProtocol, setAnesthesiaProtocol] = useState({
    asaStatus: '',
    fastingHours: '',
    plan: '',
    intraOpEvents: '',
    recovery: ''
  });

  const [activeTab, setActiveTab] = useState('surgeon');
  const [diagnoses, setDiagnoses] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        let baseSurgery = location.state?.surgery || null;
        let baseProtocol = null;

        if (!baseSurgery && surgeryId) {
          baseSurgery = await getDocument('surgeries', surgeryId);
        }

        // Fallback: el id podría ser de un protocolo que perdió la cirugía base
        if (!baseSurgery && surgeryId) {
          baseProtocol = await getDocument('surgeryProtocols', surgeryId);
          if (baseProtocol) {
            if (baseProtocol.surgeryId) {
              baseSurgery = await getDocument('surgeries', baseProtocol.surgeryId);
            }
            if (!baseSurgery) {
              baseSurgery = { ...baseProtocol, id: baseProtocol.surgeryId || surgeryId };
            }
          }
        }

        if (!baseSurgery) {
          setSnackbar({ open: true, message: 'No se encontró la cirugía', severity: 'error' });
          setLoading(false);
          return;
        }
        setSurgery(baseSurgery);

        const [existing, diagList] = await Promise.all([
          baseProtocol
            ? Promise.resolve(baseProtocol)
            : findDocumentsByField('surgeryProtocols', 'surgeryId', baseSurgery.id || surgeryId).then((arr) => arr[0] || null),
          getCollection('diagnoses'),
        ]);
        setDiagnoses(diagList.sort((a, b) => (a.diagnosis || '').localeCompare(b.diagnosis || '')));
        if (existing) {
          const doc = existing;
          setProtocolDoc(doc);
          if (doc.surgeonProtocol) {
            const sp = doc.surgeonProtocol;
            // Migración: si no existe hasComplications, inferirlo desde complications
            const hasComplications = sp.hasComplications != null
              ? sp.hasComplications
              : !!(sp.complications && sp.complications.trim());
            setSurgeonProtocol({ ...surgeonProtocol, ...sp, hasComplications });
          }
          if (doc.anesthesiaProtocol) setAnesthesiaProtocol({ ...anesthesiaProtocol, ...doc.anesthesiaProtocol });
        }
      } catch (e) {
        console.error(e);
        setSnackbar({ open: true, message: 'Error al cargar protocolo', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surgeryId, location.state]);

  const handleSave = async () => {
    if (!surgery || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const now = new Date();
      const baseData = {
        surgeryId: surgery.id || surgeryId,
        patientId: surgery.patientId || null,
        patientName: surgery.patientName || '',
        patientSpecies: surgery.patientSpecies || '',
        patientBreed: surgery.patientBreed || '',
        patientWeight: surgery.patientWeight || '',
        procedureType: surgery.procedureType || '',
        date: surgery.date || '',
        startTime: surgery.startTime || '',
        endTime: surgery.endTime || '',
        mainSurgeon: surgery.mainSurgeon || '',
        anesthesiologist: surgery.anesthesiologist || '',
        surgeonProtocol,
        anesthesiaProtocol,
        updatedAt: now,
        createdAt: protocolDoc?.createdAt || now,
        createdBy: protocolDoc?.createdBy || userData?.name || userData?.email || 'Usuario'
      };

      // Evitar duplicados: si no tenemos un protocolDoc cargado, verificar que
      // no exista ya uno para esta cirugía antes de crear otro.
      let targetId = protocolDoc?.id;
      if (!targetId) {
        const existing = await findDocumentsByField('surgeryProtocols', 'surgeryId', surgery.id || surgeryId);
        targetId = existing[0]?.id;
      }

      if (targetId) {
        await updateDocument('surgeryProtocols', targetId, baseData);
      } else {
        targetId = await addDocument('surgeryProtocols', baseData);
      }

      // Actualizar el estado con el documento guardado para futuros updates.
      setProtocolDoc({ ...baseData, id: targetId });

      const previousHistory = Array.isArray(surgery.statusHistory) ? surgery.statusHistory : [];
      await updateDocument('surgeries', surgery.id || surgeryId, {
        status: 'completada',
        statusHistory: [...previousHistory, {
          status: 'completada',
          timestamp: now,
          user: userData?.name || userData?.email || 'Usuario',
        }],
        completedAt: now,
        updatedAt: now,
      });

      setSnackbar({ open: true, message: 'Protocolo guardado y cirugía completada', severity: 'success' });
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Error al guardar protocolo', severity: 'error' });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><Typography>Cargando protocolo...</Typography></Box>;
  if (!surgery) return <Box sx={{ p: 3 }}><Typography color="error">No se encontró la cirugía.</Typography></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Protocolo Quirúrgico</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/surgery-scheduler')}>Volver</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar Protocolo'}
          </Button>
        </Box>
      </Box>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Datos del Paciente y Cirugía</Typography>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <TextField label="Paciente" value={surgery.patientName || ''} fullWidth InputProps={{ readOnly: true }} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <TextField label="Especie/Raza" value={`${surgery.patientSpecies || ''} ${surgery.patientBreed || ''}`.trim()} fullWidth InputProps={{ readOnly: true }} />
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 2
            }}>
            <TextField label="Peso (kg)" value={surgery.patientWeight || ''} fullWidth InputProps={{ readOnly: true }} />
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 2
            }}>
            <TextField label="Fecha" value={surgery.date || ''} fullWidth InputProps={{ readOnly: true }} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <TextField label="Procedimiento" value={surgery.procedureType || ''} fullWidth InputProps={{ readOnly: true }} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <TextField label="Cirujano Principal" value={surgery.mainSurgeon || ''} fullWidth InputProps={{ readOnly: true }} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <TextField label="Anestesiólogo" value={surgery.anesthesiologist || ''} fullWidth InputProps={{ readOnly: true }} />
          </Grid>
        </Grid>
      </Paper>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, value) => setActiveTab(value)}
          indicatorColor="primary"
          textColor="primary"
          sx={{ mb: 2 }}
        >
          <Tab label="Protocolo del Cirujano" value="surgeon" />
          <Tab label="Protocolo de Anestesia" value="anesthesia" />
        </Tabs>

        {activeTab === 'surgeon' && (
          <>
            <Typography variant="h6" gutterBottom>Protocolo del Cirujano</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  label="Cirujano"
                  value={surgery.mainSurgeon || ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  label="Procedimiento"
                  value={surgery.procedureType || ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <Autocomplete
                  freeSolo
                  options={diagnoses}
                  getOptionLabel={(o) => (typeof o === 'string' ? o : o.diagnosis || '')}
                  value={surgeonProtocol.preOpDiagnosis}
                  onChange={(_, v) => setSurgeonProtocol({ ...surgeonProtocol, preOpDiagnosis: typeof v === 'string' ? v : (v?.diagnosis || '') })}
                  onInputChange={(_, v, reason) => { if (reason === 'input') setSurgeonProtocol({ ...surgeonProtocol, preOpDiagnosis: v }); }}
                  filterOptions={(options, { inputValue }) => {
                    const q = inputValue.toLowerCase();
                    return options.filter((o) =>
                      (o.diagnosis || '').toLowerCase().includes(q) ||
                      (o.code || '').toLowerCase().includes(q)
                    );
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2">{option.diagnosis}</Typography>
                        {option.code && (
                          <Typography variant="caption" color="text.secondary">Código: {option.code}</Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Diagnóstico Preoperatorio" fullWidth multiline />
                  )}
                  isOptionEqualToValue={(opt, val) => (typeof val === 'string' ? opt.diagnosis === val : opt.id === val.id)}
                  noOptionsText="No se encontraron diagnósticos"
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <Autocomplete
                  freeSolo
                  options={diagnoses}
                  getOptionLabel={(o) => (typeof o === 'string' ? o : o.diagnosis || '')}
                  value={surgeonProtocol.postOpDiagnosis}
                  onChange={(_, v) => setSurgeonProtocol({ ...surgeonProtocol, postOpDiagnosis: typeof v === 'string' ? v : (v?.diagnosis || '') })}
                  onInputChange={(_, v, reason) => { if (reason === 'input') setSurgeonProtocol({ ...surgeonProtocol, postOpDiagnosis: v }); }}
                  filterOptions={(options, { inputValue }) => {
                    const q = inputValue.toLowerCase();
                    return options.filter((o) =>
                      (o.diagnosis || '').toLowerCase().includes(q) ||
                      (o.code || '').toLowerCase().includes(q)
                    );
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2">{option.diagnosis}</Typography>
                        {option.code && (
                          <Typography variant="caption" color="text.secondary">Código: {option.code}</Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Diagnóstico Postoperatorio" fullWidth multiline />
                  )}
                  isOptionEqualToValue={(opt, val) => (typeof val === 'string' ? opt.diagnosis === val : opt.id === val.id)}
                  noOptionsText="No se encontraron diagnósticos"
                />
              </Grid>
              <Grid size={12}>
                <TextField label="Descripción de la técnica" value={surgeonProtocol.description} onChange={(e) => setSurgeonProtocol({ ...surgeonProtocol, description: e.target.value })} fullWidth multiline rows={3} />
              </Grid>
              <Grid size={12}>
                <TextField label="Hallazgos intraoperatorios" value={surgeonProtocol.findings} onChange={(e) => setSurgeonProtocol({ ...surgeonProtocol, findings: e.target.value })} fullWidth multiline rows={3} />
              </Grid>
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={surgeonProtocol.hasComplications}
                      onChange={(e) => setSurgeonProtocol({
                        ...surgeonProtocol,
                        hasComplications: e.target.checked,
                        complications: e.target.checked ? surgeonProtocol.complications : '',
                      })}
                      color="error"
                    />
                  }
                  label={surgeonProtocol.hasComplications ? 'Sí hubo complicaciones' : '¿Hubo complicaciones?'}
                  sx={{ mb: surgeonProtocol.hasComplications ? 0 : 2 }}
                />
                {surgeonProtocol.hasComplications && (
                  <TextField
                    label="Descripción de la complicación"
                    value={surgeonProtocol.complications}
                    onChange={(e) => setSurgeonProtocol({ ...surgeonProtocol, complications: e.target.value })}
                    fullWidth
                    multiline
                    rows={2}
                    autoFocus
                    sx={{ mt: 1 }}
                  />
                )}
              </Grid>
              <Grid size={12}>
                <TextField label="Plan postoperatorio" value={surgeonProtocol.postOpPlan} onChange={(e) => setSurgeonProtocol({ ...surgeonProtocol, postOpPlan: e.target.value })} fullWidth multiline rows={3} />
              </Grid>
            </Grid>
          </>
        )}

        {activeTab === 'anesthesia' && (
          <>
            <Typography variant="h6" gutterBottom>Protocolo de Anestesia</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  label="Anestesiólogo"
                  value={surgery.anesthesiologist || ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  label="Procedimiento"
                  value={surgery.procedureType || ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 3
                }}>
                <TextField label="ASA" value={anesthesiaProtocol.asaStatus} onChange={(e) => setAnesthesiaProtocol({ ...anesthesiaProtocol, asaStatus: e.target.value })} fullWidth />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 3
                }}>
                <TextField label="Horas de ayuno" value={anesthesiaProtocol.fastingHours} onChange={(e) => setAnesthesiaProtocol({ ...anesthesiaProtocol, fastingHours: e.target.value })} fullWidth />
              </Grid>
              <Grid size={12}>
                <TextField label="Plan anestésico" value={anesthesiaProtocol.plan} onChange={(e) => setAnesthesiaProtocol({ ...anesthesiaProtocol, plan: e.target.value })} fullWidth multiline rows={3} />
              </Grid>
              <Grid size={12}>
                <TextField label="Eventos intraoperatorios" value={anesthesiaProtocol.intraOpEvents} onChange={(e) => setAnesthesiaProtocol({ ...anesthesiaProtocol, intraOpEvents: e.target.value })} fullWidth multiline rows={3} />
              </Grid>
              <Grid size={12}>
                <TextField label="Recuperación" value={anesthesiaProtocol.recovery} onChange={(e) => setAnesthesiaProtocol({ ...anesthesiaProtocol, recovery: e.target.value })} fullWidth multiline rows={3} />
              </Grid>
            </Grid>
          </>
        )}
      </Paper>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SurgicalProtocol;
